import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllPayments } from "../../store/thunks/paymentsThunk";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import InvoicePaymentModal from "../../modals/InvoicePaymentModal";
import Table from "../../applicationUi/components/Table";
import QuickBooksLogo from "../../assets/quickbooks-1.svg";

const PaymentsPage = () => {
  const dispatch = useDispatch();

  // Safeguard Redux state with null-safe defaults
  const payments = useSelector((state) => state.payments?.payments || []);
  const isLoading = useSelector((state) => state.payments?.status === "loading");
  const currentPage = useSelector((state) => state.payments?.currentPage || 1);
  const totalPages = useSelector((state) => state.payments?.totalPages || 1);
  const totalPayments = useSelector((state) => state.payments?.totalPayments || 0);
  const error = useSelector((state) => state.payments?.error || null);

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });
  const [dateFilter, setDateFilter] = useState("7"); // Default to 7 days

  // Logging for debugging
  console.log("currentPage", currentPage);
  console.log("totalPages", totalPages);
  console.log("totalPayments", totalPayments);
  console.log("payments", payments);

  useEffect(() => {
    dispatch(fetchAllPayments({ 
      page: currentPage,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      dateFilter: dateFilter
    }));
  }, [dispatch, currentPage, sortConfig, dateFilter]);

  const handleRowClick = (clickedPaymentId) => {
    const payment = payments.find((pay) => pay?._id === clickedPaymentId);
    if (payment) {
      setSelectedPayment(payment);
      setShowPaymentModal(true);
    }
  };

  const handleModalClose = () => {
    setShowPaymentModal(false);
    setSelectedPayment(null);
  };

  const handlePageChange = (newPage) => {
    dispatch(fetchAllPayments({ 
      page: newPage,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      dateFilter: dateFilter
    }));
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
    console.log("sortConfig", sortConfig);
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
  };

  if (error) {
    return <div>Error: {typeof error === "string" ? error : "An unknown error occurred."}</div>;
  }

  return (
    <div>
      {isLoading ? (
        <p>Loading payments...</p>
      ) : (
        <div>
          <div className="py-4 flex justify-between items-center">
            <div className="flex-1 mr-4">
              <SearchDropdown
                prefix=":payment:"
                displayFields={[
                  "invoiceId.invoiceId",
                  "paymentMethod",
                  "amount",
                  "invoiceId.client.fullName",
                ]}
                onSelection={(selectedPayment) => {
                  setSelectedPayment(selectedPayment);
                  setShowPaymentModal(true);
                }}
                placeholder={
                  "Search for a payment by invoice ID, client name, or payment method"
                }
              />
            </div>
            <div className="w-48">
              <select
                value={dateFilter}
                onChange={handleDateFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-sm shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>

          <Table
            columns={[
              {
                header: "Invoice IDs",
                accessor: (row) => {
                  if (!row?.invoiceId?.length) return "N/A";
                  return row.invoiceId.map(inv => inv.invoiceId).join(", ");
                },
                sortable: true,
              },
              {
                header: "Client Names",
                accessor: (row) => {
                  if (!row?.invoiceId?.length) return "N/A";
                  const clientNames = row.invoiceId.map(inv => {
                    const client = inv.client;
                    return client ? `${client.firstName} ${client.lastName}` : "N/A";
                  });
                  return [...new Set(clientNames)].join(", "); // Remove duplicates
                },
              },
              {
                header: "Amount",
                accessor: (row) => `$${row?.amount?.toFixed(2) || "0.00"}`,
                sortable: true,
              },
              {
                header: "Payment Date",
                accessor: (row) => {
                  if (!row?.paymentDate) return "N/A";
                  const date = new Date(row.paymentDate);
                  return date.toLocaleDateString("en-US", {
                    year: "numeric",
                    weekday: 'short',
                    month: "short",
                    day: "numeric",
                  });
                },
                sortable: true,
              },
              {
                header: "Payment Method",
                accessor: (row) => row?.paymentMethod || "N/A",
                sortable: true,
              },
              {
                header: "QuickBooks",
                accessor: (row) => (
                  row?.QBOPaymentId ? (
                    <img
                      src={QuickBooksLogo}
                      alt="QuickBooks"
                      className="ml-2 h-5 w-5"
                      title="Loaded to QuickBooks"
                    />
                  ) : null
                ),
              },
            ]}
            data={payments}
            onRowClick={handleRowClick}
            title="Payments"
            description={`List of all payments (${totalPayments} total)`}
            showPagination={true}
            currentPage={currentPage}
            totalResults={totalPayments}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            sortConfig={sortConfig}
            onSortChange={handleSort}
          />

          {showPaymentModal && selectedPayment && (
            <InvoicePaymentModal
              payment={selectedPayment || {}}
              onClose={handleModalClose}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
